import React, { useCallback, useEffect, useState } from 'react'
import { Button } from 'reactstrap'
import ICSErrorCard from '../../../../components/ICSErrorCard/ICSErrorCard'
import { LoadIndicator } from 'devextreme-react'
import { useAppSelector } from '../../../../hooks/useAppSelector'
import {
  deleteFiles,
  DeleteFilesResponse
} from '../../../../services/endpoints/data'
import { useDispatch } from 'react-redux'
import { dataSlice } from '../../../../redux/slices'
import useAsyncService from '../../../../hooks/useAsyncService'
import RenderDataModal from '../RenderViewModal/RenderViewModal'
import { Container, Row, Col } from 'reactstrap'
import { ShowDataGrid } from '../SelectedFilesGrid/ShowDataGrid'
import './DeleteSelectionModal.css'
interface SelectedFile {
  id: string
  displayName: string
  startDate: string
  endDate: string
  extension: string
  vehicle: { name: string, displayName: string }
}
interface DeleteSelectionsModalProps {
  onClose: (deleteCompleted: boolean) => void
}

const _formatFileIds = (fileIds: number[]): string => fileIds.join(', ')

export const DeleteModal: React.FC<DeleteSelectionsModalProps> = ({
  onClose
}) => {
  const [deleteStarted, setDeleteStarted] = useState(false)
  const [deleteCompleted, setDeleteCompleted] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([])
  const versions = useAppSelector((State) => State.data.versions)

  const handleDeleteSuccess = useCallback(() => {
    setDeleteCompleted(true)
  }, [])

  useEffect(() => {
    const selectedFiles = Object.entries(versions).reduce<SelectedFile[]>(
      (acc, [id, version]) => {
        if (version?.selected) {
          acc.push({
            id, // Extract the key as ID
            displayName: version.fileName,
            startDate: version.startDate,
            endDate: version.endDate,
            extension: version.type,
            vehicle: { name: version.vehicleName, displayName: version.vehicleDisplayName }
          })
        }
        return acc
      },
      []
    )
    setSelectedFiles(selectedFiles)
  }, [versions])

  const getBody = () => {
    if (!deleteStarted) {
      return (
        <>
          <_showDeleteWarning selectedFiles={selectedFiles} />
          <ShowDataGrid selectedFiles={selectedFiles} />
        </>
      )
    } else {
      return (
        <_DeleteSpinner
          selectedFiles={selectedFiles}
          onDeleteSuccess={handleDeleteSuccess}
        />
      )
    }
  }

  const handleClose = () => {
    onClose(deleteCompleted)
  }

  return (
    <RenderDataModal
      onClose={handleClose}
      title="Selected Files"
      className="delete-selection-modal"
      additionalButtons={
        !deleteStarted && selectedFiles.length > 0
          ? [
              <_createDeleteButton
                key="delete-button"
                selectedFiles={selectedFiles}
                setDeleting={setDeleteStarted}
              />,
              <_createClearAllButton
                key="clear-button"
                selectedFiles={selectedFiles}
              />
            ]
          : []
      }
    >
      <Container className="delete-selections-container">
        <Row className="delete-selections-">
          <Col>{getBody()}</Col>
        </Row>
      </Container>
    </RenderDataModal>
  )
}

const _showDeleteWarning: React.FC<{ selectedFiles: SelectedFile[] }> = ({
  selectedFiles
}) => {
  return (
    <span
      className={`delete-alert ${
        selectedFiles.length === 0 ? 'alert-warning' : 'alert-danger'
      }`}
    >
      {selectedFiles.length === 0 || !selectedFiles
        ? 'No files selected.'
        : 'Files will be marked end-of-life and can only be recovered by an administrator.'}
    </span>
  )
}

const _DeleteSpinner: React.FC<{
  selectedFiles: SelectedFile[]
  onDeleteSuccess: () => void
}> = ({ selectedFiles, onDeleteSuccess }) => {
  const dispatch = useDispatch()
  const { serviceLoading, serviceValue, serviceError, setServiceArgs } =
    useAsyncService({
      serviceCall: deleteFilesService,
      delay: 500,
      initialArgs: null
    })

  useEffect(() => {
    const fileIds = selectedFiles.map((file) => file.id)
    if (fileIds.length > 0) setServiceArgs(fileIds)
  }, [selectedFiles, setServiceArgs])

  useEffect(() => {
    if (serviceValue && serviceValue.deleted > 0) {
      dispatch(dataSlice.actions.clearAllSelections())
      onDeleteSuccess()
    }
  }, [dispatch, onDeleteSuccess, serviceValue])

  if (serviceError) {
    return <ICSErrorCard code={'error'} message={serviceError} fields={[]} />
  }
  if (serviceLoading) {
    return <LoadIndicator />
  }
  if (serviceValue) {
    const hasFailures =
      serviceValue.notFound.length > 0 || serviceValue.unauthorized.length > 0

    return (
      <div
        className={`delete-alert ${hasFailures ? 'alert-warning' : 'alert-success'}`}
      >
        <div>
          {serviceValue.deleted} of {serviceValue.requested} requested files were
          marked for deletion.
        </div>
        {serviceValue.notFound.length > 0 && (
          <div>Not found: {_formatFileIds(serviceValue.notFound)}</div>
        )}
        {serviceValue.unauthorized.length > 0 && (
          <div>
            Unauthorized: {_formatFileIds(serviceValue.unauthorized)}
          </div>
        )}
      </div>
    )
  }
  return <></>
}

const _createClearAllButton: React.FC<{ selectedFiles: SelectedFile[] }> = ({
  selectedFiles
}) => {
  const dispatch = useDispatch()
  const clearAllSelections = () => {
    dispatch(dataSlice.actions.clearAllSelections())
  }

  return (
    <div>
      <Button
        className="modal-btn clear-btn"
        onClick={clearAllSelections}
        disabled={selectedFiles.length == 0}
      >
        <span className="glyphicons glyphicons-ban-circle"></span> Clear
        Selections
      </Button>
    </div>
  )
}

const _createDeleteButton: React.FC<{
  selectedFiles: SelectedFile[]
  setDeleting: (value: boolean) => void
}> = ({ selectedFiles, setDeleting }) => {
  const handleDeleteSelections = () => {
    setDeleting(true)
  }
  return (
    <div>
      <Button
        className="modal-btn delete-btn"
        onClick={handleDeleteSelections}
        disabled={selectedFiles.length == 0}
      >
        <span className="glyphicons glyphicons-bin"></span>
        {'Move to Recycling'}
      </Button>
    </div>
  )
}

const deleteFilesService = async (
  fileIds: string[]
): Promise<DeleteFilesResponse> => {
  try {
    const numericFileIds = fileIds.map(Number)

    if (numericFileIds.some(Number.isNaN)) {
      throw new Error('One or more file IDs are invalid')
    }

    const results = await deleteFiles(numericFileIds)
    return results
  } catch (err) {
    throw new Error('One or more files failed to delete')
  }
}
